import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { applications } from '../api';
import StatusBadge from '../components/StatusBadge';
import VorlagenBereich from '../components/VorlagenBereich';

type Reiter = 'vorlagen' | 'felder' | 'releases' | 'produkte';

/**
 * Eine Application: die Software, die ausgerollt wird.
 *
 * Seit Stufe 2 (10.10.2026) wird hier gepflegt, was vorher am Produkt hing:
 * Vorlagen, Einstellungen (Konfigurationsfelder) und Releases. Das Produkt
 * sagt nur noch, welche Applications es verkauft und mit welchen Vorgaben.
 */
export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reiter, setReiter] = useState<Reiter>('vorlagen');
  const { data: app, isLoading } = useQuery({
    queryKey: ['applications', id],
    queryFn: () => applications.get(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-48 rounded bg-slate-200" />
        <div className="h-4 w-96 rounded bg-slate-100" />
      </div>
    );
  }
  if (!app) return <div className="text-slate-500">Application nicht gefunden.</div>;

  const reiterListe: { key: Reiter; label: string; zahl?: number }[] = [
    { key: 'vorlagen', label: 'Vorlagen', zahl: app.templates.length },
    { key: 'felder', label: 'Einstellungen', zahl: app.configFields.length },
    { key: 'releases', label: 'Releases', zahl: app.releases.length },
    { key: 'produkte', label: 'Verkauft als', zahl: app.products.length },
  ];

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/applications')}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={14} /> Alle Applications
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">{app.name}</h1>
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{app.slug}</code>
        </div>
        {app.description && <p className="mt-1 max-w-3xl text-sm text-slate-500">{app.description}</p>}
        <p className="mt-2 text-xs text-slate-500">
          {app._count?.instances ?? 0} Instanz(en) · Vorlagen, Einstellungen und Releases gelten für jede davon, gleich über welches Produkt sie gekauft wurde.
        </p>
      </div>

      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {reiterListe.map((r) => (
            <button
              key={r.key}
              onClick={() => setReiter(r.key)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                reiter === r.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {r.label}
              {r.zahl !== undefined && <span className="ml-1.5 text-xs tabular-nums text-slate-400">{r.zahl}</span>}
            </button>
          ))}
        </div>
      </div>

      {reiter === 'vorlagen' && (
        <div className="space-y-3">
          <VorlagenBereich applicationId={app.id} />
        </div>
      )}

      {reiter === 'felder' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-surface">
          {app.configFields.length === 0 ? (
            <p className="p-5 text-sm italic text-slate-400">Diese Application fragt beim Anlegen keine eigenen Einstellungen ab.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Schlüssel</th>
                  <th className="px-4 py-2 font-medium">Bezeichnung</th>
                  <th className="px-4 py-2 font-medium">Vorgabe</th>
                  <th className="px-4 py-2 font-medium">Eigenschaften</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {app.configFields.map((f) => (
                  <tr key={f.id}>
                    <td className="px-4 py-2 font-mono text-xs">{f.key}</td>
                    <td className="px-4 py-2">{f.label}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{f.defaultValue ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {[f.required && 'Pflicht', f.isSecret && 'geheim', f.visibleInCC && 'im Kundenportal'].filter(Boolean).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
            Was ein Produkt an diesen Feldern voreinstellt, steht am Produkt unter „Applications“.
          </p>
        </div>
      )}

      {reiter === 'releases' && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Link to="/releases" className="text-sm font-medium text-brand-600 hover:text-brand-800">Zu den Releases →</Link>
          </div>
          {app.releases.length === 0 ? (
            <p className="text-sm italic text-slate-400">Noch kein Release für diese Application.</p>
          ) : (
            <div className="grid gap-2">
              {app.releases.map((r) => (
                <div key={r.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-surface px-4 py-3 text-sm">
                  <span className="font-medium text-slate-900">v{r.version}</span>
                  <StatusBadge status={r.status as never} type="release" />
                  <span className="text-xs text-slate-500">{r.releaseChannel}</span>
                  <span className="ml-auto text-xs tabular-nums text-slate-400">{new Date(r.createdAt).toLocaleDateString('de-DE')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {reiter === 'produkte' && (
        <div className="grid gap-2">
          {app.products.length === 0 ? (
            <p className="text-sm italic text-slate-400">Kein Produkt verkauft diese Application — sie dient nur dem Betrieb.</p>
          ) : (
            app.products.map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-surface px-4 py-3 text-sm hover:border-brand-300"
              >
                <span className="font-medium text-slate-900">{p.name}</span>
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{p.slug}</code>
                <StatusBadge status={p.status} type="product" />
                {!p.imVerkauf && <span className="text-xs text-slate-400">nicht im Verkauf</span>}
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
